import { trackEvent } from '@patrianna/analytics'
import { Form, FormTextField } from '@patrianna/core-components'
import { useMediaQuery } from '@patrianna/shared-hooks'
import type { LoginFormValues } from '@patrianna/shared-patrianna-types/store/AuthModule'
import { getAuthFlowLoadingSelector } from '@patrianna/shared-store/auth'
import { nextTick } from '@patrianna/shared-utils'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { usePathname, useSearchParams } from 'next/navigation'
import { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'

import Button from 'components/Button'
import Recaptcha from 'components/Recaptcha'
import { useRouterPushWithCallback } from 'containers/NavigationProvider'
import formValidatorsContent from 'prismic-translates/form-validators.json'
import content from 'prismic-translates/loginpage.json'
import { FormPasswordField } from 'src/components/Form/FormPasswordField'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { login } from 'src/store/modules/auth/actions'
import { emailValidatorHookForm } from 'src/utils/formValidators'
import { getPasswordPolicySelector } from 'store/modules/appConfig/selectors'

import { ManualLoginContext } from '../AuthDialogLayout'

import classes from './styles.module.scss'

const t = getTranslationFromJson([content, formValidatorsContent])

const defaultValues: LoginFormValues = {
  email: '',
  password: '',
}

function LoginForm() {
  const { loginPassed, setLoginPassed } = useContext(ManualLoginContext)
  const [recaptchaToken, setRecaptchaToken] = useState<string>(null)
  const [shouldReset, setShouldReset] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const routerPush = useRouterPushWithCallback()
  const query = useSearchParams()
  const pathname = usePathname()
  const passwordPolicy = useAppSelector(getPasswordPolicySelector)
  const isLoading = useAppSelector(getAuthFlowLoadingSelector)
  const dispatch = useAppDispatch()
  const isMobile = useMediaQuery('xs')
  const methods = useForm<LoginFormValues>({ mode: 'onTouched', defaultValues })

  const {
    formState: { isValid },
  } = methods

  const onError = () => {
    setShouldReset(true)
  }

  const handleLogin = (values: LoginFormValues) => {
    setIsSubmitting(true)
    nextTick().then(() => {
      setShouldReset(false)

      dispatch(
        login({
          form: values,
          routerPush,
          routerQuery: query,
          routerPath: pathname,
          setSubmitting: setIsSubmitting,
          setLoginPassed,
          onError,
          isMobile,
          captchaToken: recaptchaToken,
        }),
      )
    })
  }

  const handleReCaptcha = (token: string) => {
    setRecaptchaToken(token)
  }

  return (
    <Form methods={methods} onSubmit={handleLogin} className={classes.root}>
      <FormTextField
        name='email'
        className={classes.input}
        placeholder={t('emailInput')}
        rules={emailValidatorHookForm(t('enter_valid_email_address'), t('enter_email_address'))}
      />

      <FormPasswordField
        name='password'
        helperText=''
        placeholder={t('password')}
        className={classes.input}
        rules={{
          required: t('required'),
          minLength: { value: passwordPolicy?.min, message: t('min_8_sybmols') },
          maxLength: { value: passwordPolicy?.max, message: t('max_length_20_symbols') },
        }}
        withHint={false}
      />

      <div className={classes.actions}>
        <Recaptcha verifyCallback={handleReCaptcha} reset={shouldReset} className={classes.recaptcha} withFakeCaptcha />

        <Button
          type='submit'
          className={classes.button}
          disabled={!isValid || isSubmitting || isLoading || loginPassed || !recaptchaToken}
          data-test='login-now-btn'
          onClick={() => trackEvent('login_page_manual_login_button')}
        >
          {t('loginButtonLabel')}
        </Button>
      </div>
    </Form>
  )
}

export default LoginForm
